import React, { useEffect, useState, useRef } from 'react';
import { mbaAPI, getImageUrl } from '../../utils/api';
import '../../styles/AdminUniversities.scss';

const AdminUniversities = () => {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUni, setEditingUni] = useState(null);

  // Form states
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [logo, setLogo] = useState('🎓');
  const [logoType, setLogoType] = useState('emoji'); // 'emoji' or 'upload'
  const [guidelines, setGuidelines] = useState('');
  const [reportFormat, setReportFormat] = useState('');
  const [indexFormat, setIndexFormat] = useState('');
  const [certificateFormat, setCertificateFormat] = useState('');
  const [instructions, setInstructions] = useState('');
  
  // Legacy fields (optional support)
  const [formatting, setFormatting] = useState('');
  const [structure, setStructure] = useState('');
  const [submission, setSubmission] = useState('');
  const [viva, setViva] = useState('');
  const [evaluation, setEvaluation] = useState('');
  
  const [isActive, setIsActive] = useState(true);

  // Guidelines Document upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Sample Report upload state
  const [selectedSampleFile, setSelectedSampleFile] = useState(null);
  const [uploadingSample, setUploadingSample] = useState(false);
  const sampleFileInputRef = useRef(null);

  // Custom Logo upload state
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Quick upload in table state
  const quickFileInputRefs = useRef({});
  const quickSampleFileInputRefs = useRef({});

  const loadUniversities = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await mbaAPI.adminGetUniversities();
      setUniversities(response.data?.data || []);
    } catch (err) {
      setError('Failed to load universities list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUniversities();
  }, []);

  const resetForm = () => {
    setName('');
    setSlug('');
    setLogo('🎓');
    setLogoType('emoji');
    setGuidelines('');
    setReportFormat('');
    setIndexFormat('');
    setCertificateFormat('');
    setInstructions('');
    
    // Legacy fields
    setFormatting('');
    setStructure('');
    setSubmission('');
    setViva('');
    setEvaluation('');
    
    setIsActive(true);
    setSelectedFile(null);
    setSelectedSampleFile(null);
    setSelectedLogoFile(null);
    setEditingUni(null);
  };

  const handleEdit = (uni) => {
    setEditingUni(uni);
    setName(uni.name || '');
    setSlug(uni.slug || '');
    setLogo(uni.logo || '🎓');
    
    // Determine logo type
    const isCustomLogo = uni.logo && (uni.logo.startsWith('/') || uni.logo.startsWith('http'));
    setLogoType(isCustomLogo ? 'upload' : 'emoji');
    
    setGuidelines(uni.guidelines || '');
    setReportFormat(uni.reportFormat || '');
    setIndexFormat(uni.indexFormat || '');
    setCertificateFormat(uni.certificateFormat || '');
    setInstructions(uni.instructions || '');
    
    // Legacy fields
    setFormatting(uni.formatting || '');
    setStructure(uni.structure || '');
    setSubmission(uni.submission || '');
    setViva(uni.viva || '');
    setEvaluation(uni.evaluation || '');
    
    setIsActive(uni.is_active !== false);
    setSelectedFile(null);
    setSelectedSampleFile(null);
    setSelectedLogoFile(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this university guidelines entry? This action cannot be undone.')) return;
    try {
      setError('');
      setSuccess('');
      await mbaAPI.adminDeleteUniversity(id);
      setSuccess('University guidelines deleted successfully.');
      setUniversities(universities.filter((u) => u.id !== id));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete university guidelines');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('University name is required');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setLoading(true);

      const uniData = {
        name,
        slug: slug.trim() || null,
        logo: logoType === 'emoji' ? logo : (editingUni?.logo || '🎓'), // keep existing upload or default if none
        guidelines: guidelines.trim() || null,
        reportFormat: reportFormat.trim() || null,
        indexFormat: indexFormat.trim() || null,
        certificateFormat: certificateFormat.trim() || null,
        instructions: instructions.trim() || null,
        
        // Legacy fields
        formatting: formatting.trim() || null,
        structure: structure.trim() || null,
        submission: submission.trim() || null,
        viva: viva.trim() || null,
        evaluation: evaluation.trim() || null,
        
        is_active: isActive
      };

      let uniId = editingUni?.id;

      if (editingUni) {
        await mbaAPI.adminUpdateUniversity(editingUni.id, uniData);
        // Upload logo file if selected and in upload mode
        if (logoType === 'upload' && selectedLogoFile) {
          const formData = new FormData();
          formData.append('file', selectedLogoFile);
          const uploadRes = await mbaAPI.adminUploadUniversityLogo(editingUni.id, formData);
          if (uploadRes.data?.logo) {
            uniData.logo = uploadRes.data.logo;
          }
        }
        setSuccess('University guidelines updated successfully.');
      } else {
        const res = await mbaAPI.adminCreateUniversity(uniData);
        uniId = res.data?.id;
        // Upload logo file if selected and in upload mode
        if (uniId && logoType === 'upload' && selectedLogoFile) {
          const formData = new FormData();
          formData.append('file', selectedLogoFile);
          await mbaAPI.adminUploadUniversityLogo(uniId, formData);
        }
        setSuccess('University guidelines created successfully.');
      }

      await loadUniversities();
      setShowForm(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save university guidelines');
    } finally {
      setLoading(false);
    }
  };

  // Handles custom logo file upload when editing
  const handleLogoUploadDirect = async () => {
    if (!editingUni || !selectedLogoFile) return;
    try {
      setUploadingLogo(true);
      setError('');
      setSuccess('');
      const formData = new FormData();
      formData.append('file', selectedLogoFile);
      const response = await mbaAPI.adminUploadUniversityLogo(editingUni.id, formData);
      if (response.data?.success) {
        setSuccess('Logo uploaded successfully.');
        setLogo(response.data.logo);
        setSelectedLogoFile(null);
        await loadUniversities();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handles guidelines PDF/doc upload
  const handleDocumentUpload = async (uniId) => {
    const fileToUpload = selectedFile;
    if (!fileToUpload) {
      setError('Please select a file to upload first.');
      return;
    }

    const fileExt = fileToUpload.name.split('.').pop().toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(fileExt)) {
      setError('Only PDF, DOC, and DOCX files are allowed.');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('file', fileToUpload);

      const response = await mbaAPI.adminUploadUniversityDoc(uniId, formData);
      if (response.data?.success) {
        setSuccess('Guidelines document uploaded successfully.');
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        
        // Update editing instance doc URL
        if (editingUni && editingUni.id === uniId) {
          setEditingUni({ ...editingUni, document_url: response.data.document_url });
        }
        
        await loadUniversities();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Handles Sample Report PDF/doc upload
  const handleSampleReportUpload = async (uniId) => {
    const fileToUpload = selectedSampleFile;
    if (!fileToUpload) {
      setError('Please select a sample report file to upload first.');
      return;
    }

    const fileExt = fileToUpload.name.split('.').pop().toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(fileExt)) {
      setError('Only PDF, DOC, and DOCX files are allowed.');
      return;
    }

    try {
      setUploadingSample(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('file', fileToUpload);

      const response = await mbaAPI.adminUploadUniversitySampleReport(uniId, formData);
      if (response.data?.success) {
        setSuccess('Sample report uploaded successfully.');
        setSelectedSampleFile(null);
        if (sampleFileInputRef.current) sampleFileInputRef.current.value = '';
        
        // Update editing instance sample URL
        if (editingUni && editingUni.id === uniId) {
          setEditingUni({ ...editingUni, sample_report_url: response.data.sample_report_url });
        }
        
        await loadUniversities();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload sample report');
    } finally {
      setUploadingSample(false);
    }
  };

  // Handles direct file upload from the main list row
  const handleQuickUpload = async (uniId, file) => {
    if (!file) return;
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(fileExt)) {
      setError('Only PDF, DOC, and DOCX files are allowed.');
      return;
    }

    try {
      setError('');
      setSuccess('');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await mbaAPI.adminUploadUniversityDoc(uniId, formData);
      if (response.data?.success) {
        setSuccess('Guidelines document uploaded successfully.');
        await loadUniversities();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload document');
    }
  };

  // Handles direct sample upload from the main list row
  const handleQuickSampleUpload = async (uniId, file) => {
    if (!file) return;
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(fileExt)) {
      setError('Only PDF, DOC, and DOCX files are allowed.');
      return;
    }

    try {
      setError('');
      setSuccess('');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await mbaAPI.adminUploadUniversitySampleReport(uniId, formData);
      if (response.data?.success) {
        setSuccess('Sample report uploaded successfully.');
        await loadUniversities();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload sample report');
    }
  };

  const getFullDocUrl = (relativeUrl) => {
    if (!relativeUrl) return '';
    return getImageUrl(relativeUrl);
  };

  const renderUniLogo = (uni) => {
    const l = uni.logo;
    if (!l) return <span style={{ fontSize: '24px' }}>🎓</span>;
    if (l.startsWith('/') || l.startsWith('http')) {
      return <img src={getFullDocUrl(l)} alt={uni.name} style={{ width: '32px', height: '32px', objectFit: 'contain', background: '#f8fafc', padding: '2px', borderRadius: '6px', border: '1px solid #e2e8f0' }} />;
    }
    return <span style={{ fontSize: '24px' }}>{l}</span>;
  };

  return (
    <div className="admin-universities-container">
      {showForm ? (
        <div className="uni-form-card">
          <div className="form-header">
            <h3>{editingUni ? `✏️ Edit Guidelines: ${editingUni.name}` : '➕ Add University Guidelines'}</h3>
            <button className="close-btn" onClick={() => { setShowForm(false); resetForm(); }}>&times;</button>
          </div>

          {error && <div className="form-error">{error}</div>}
          {success && <div style={{ color: 'green', background: '#dcfce7', padding: '12px', borderRadius: '10px', marginBottom: '16px' }}>{success}</div>}

          <form onSubmit={handleFormSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>University Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Savitribai Phule Pune University (SPPU)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>URL Slug (Optional - will generate automatically if empty)</label>
                <input
                  type="text"
                  placeholder="e.g. sppu"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                />
              </div>

              <div className="form-group">
                <label>Logo Type</label>
                <select value={logoType} onChange={(e) => setLogoType(e.target.value)}>
                  <option value="emoji">Emoji Icon</option>
                  <option value="upload">Upload Custom Image</option>
                </select>
              </div>

              {logoType === 'emoji' ? (
                <div className="form-group">
                  <label>Select Emoji Logo</label>
                  <select value={logo} onChange={(e) => setLogo(e.target.value)}>
                    <option value="🎓">🎓 Graduation Cap</option>
                    <option value="🏛️">🏛️ Classical Building</option>
                    <option value="🏫">🏫 School/Campus</option>
                    <option value="📚">📚 Books</option>
                    <option value="🌟">🌟 Star</option>
                    <option value="⚙️">⚙️ Tech / Gear</option>
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>Custom Image Logo</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg,.gif"
                      onChange={(e) => setSelectedLogoFile(e.target.files[0])}
                    />
                    {editingUni && (
                      <button
                        type="button"
                        className="upload-btn"
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                        onClick={handleLogoUploadDirect}
                        disabled={uploadingLogo || !selectedLogoFile}
                      >
                        {uploadingLogo ? 'Uploading...' : 'Upload Now'}
                      </button>
                    )}
                  </div>
                  {logo && (logo.startsWith('/') || logo.startsWith('http')) && (
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '6px' }}>
                      <span style={{ fontSize: '11px', color: '#64748b' }}>Current: </span>
                      <img src={getFullDocUrl(logo)} alt="Logo" style={{ height: '24px', marginLeft: '6px', objectFit: 'contain' }} />
                    </div>
                  )}
                </div>
              )}

              {/* Core Formats (New columns) */}
              <div className="form-group full-width" style={{ marginTop: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                <h4 style={{ margin: '0 0 4px 0', color: '#0f766e' }}>Core Guidelines Config (Recommended)</h4>
              </div>

              <div className="form-group full-width">
                <label>Guidelines (One guideline/rule per line)</label>
                <textarea
                  placeholder="e.g. Eligibility: Students who have cleared semester 2.&#10;Objectives: Focuses on core business strategy."
                  value={guidelines}
                  onChange={(e) => setGuidelines(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="form-group full-width">
                <label>Report Format (One format details per line)</label>
                <textarea
                  placeholder="e.g. Paper Size: A4 size white paper&#10;Font Family: Times New Roman, 12 pt for body"
                  value={reportFormat}
                  onChange={(e) => setReportFormat(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="form-group full-width">
                <label>Index / Structure Format (One chapter per line)</label>
                <textarea
                  placeholder="e.g. Chapter 1: Introduction&#10;Chapter 2: Review of Literature&#10;Chapter 3: Methodology"
                  value={indexFormat}
                  onChange={(e) => setIndexFormat(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="form-group full-width">
                <label>Certificate Format (One item per line)</label>
                <textarea
                  placeholder="e.g. Guide / Advisor Certificate&#10;External Examiner Approval page"
                  value={certificateFormat}
                  onChange={(e) => setCertificateFormat(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="form-group full-width">
                <label>Instructions (One instruction per line)</label>
                <textarea
                  placeholder="e.g. Avoid plagiarism above 15%.&#10;Submit soft copy on a CD."
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Legacy Formats (Optional columns) */}
              <div className="form-group full-width" style={{ marginTop: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                <h4 style={{ margin: '0 0 4px 0', color: '#64748b' }}>Legacy Formats (For backward compatibility)</h4>
              </div>

              <div className="form-group full-width">
                <label>Formatting Rules (One rule per line)</label>
                <textarea
                  placeholder="e.g. 1.5 line spacing throughout the report"
                  value={formatting}
                  onChange={(e) => setFormatting(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="form-group full-width">
                <label>Report Structure (One chapter per line)</label>
                <textarea
                  placeholder="e.g. Executive Summary&#10;Chapter 1: Objectives"
                  value={structure}
                  onChange={(e) => setStructure(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="form-group full-width">
                <label>Submission Instructions (One step per line)</label>
                <textarea
                  placeholder="e.g. Submit two hard copies to the department"
                  value={submission}
                  onChange={(e) => setSubmission(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="form-group full-width">
                <label>Viva Guidelines (One rule per line)</label>
                <textarea
                  placeholder="e.g. 10 to 12 slides PPT covering all chapters"
                  value={viva}
                  onChange={(e) => setViva(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="form-group full-width">
                <label>Evaluation Criteria (One item per line)</label>
                <textarea
                  placeholder="e.g. Project Guide Evaluation: 50 Marks"
                  value={evaluation}
                  onChange={(e) => setEvaluation(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <span>Active & Visible on Public Page</span>
                </label>
              </div>
            </div>

            {/* Document Upload Area (Only for existing universities) */}
            {editingUni && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '24px', borderTop: '1px solid #f1f5f9', paddingTop: '24px' }}>
                {/* Guidelines File */}
                <div className="doc-upload-section">
                  <h4>📄 Official Guidelines Document (PDF/DOC/DOCX)</h4>
                  
                  {editingUni.document_url && (
                    <div className="current-doc">
                      Current: <a href={getFullDocUrl(editingUni.document_url)} target="_blank" rel="noopener noreferrer">Download / View</a>
                    </div>
                  )}

                  <div className="upload-controls">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      ref={fileInputRef}
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                    />
                    <button
                      type="button"
                      className="upload-btn"
                      onClick={() => handleDocumentUpload(editingUni.id)}
                      disabled={uploading || !selectedFile}
                    >
                      {uploading ? 'Uploading...' : 'Upload Guidelines'}
                    </button>
                  </div>
                </div>

                {/* Sample Report File */}
                <div className="doc-upload-section">
                  <h4>📁 Sample Project Report (PDF/DOC/DOCX)</h4>
                  
                  {editingUni.sample_report_url && (
                    <div className="current-doc">
                      Current: <a href={getFullDocUrl(editingUni.sample_report_url)} target="_blank" rel="noopener noreferrer">Download / View</a>
                    </div>
                  )}

                  <div className="upload-controls">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      ref={sampleFileInputRef}
                      onChange={(e) => setSelectedSampleFile(e.target.files[0])}
                    />
                    <button
                      type="button"
                      className="upload-btn"
                      onClick={() => handleSampleReportUpload(editingUni.id)}
                      disabled={uploadingSample || !selectedSampleFile}
                    >
                      {uploadingSample ? 'Uploading...' : 'Upload Sample Report'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn-secondary" style={{ marginRight: '10px' }} onClick={() => { setShowForm(false); resetForm(); }}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Saving...' : editingUni ? 'Save Changes' : 'Create Guidelines'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <>
          <div className="uni-header">
            <div>
              <h2>🎓 MBA University Formats</h2>
              <p>Manage guideline formats, report structures (index), evaluation metrics, official documents, and sample project reports for each university.</p>
            </div>
            <button
              className="btn-primary"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
            >
              ➕ Add University Guidelines
            </button>
          </div>

          {error && <div className="form-error">{error}</div>}
          {success && <div style={{ color: 'green', background: '#dcfce7', padding: '12px', borderRadius: '10px', marginBottom: '16px' }}>{success}</div>}

          <div className="uni-table-card">
            {loading ? (
              <div className="loading">Loading university formats...</div>
            ) : universities.length === 0 ? (
              <div className="empty-state">No universities configured yet.</div>
            ) : (
              <div className="uni-table">
                <div className="uni-table-head">
                  <span style={{ textAlign: 'center' }}>Logo</span>
                  <span>University Name</span>
                  <span>Status</span>
                  <span>Guidelines Doc</span>
                  <span>Sample Report</span>
                  <span>Actions</span>
                </div>
                {universities.map((uni) => (
                  <div key={uni.id} className="uni-table-row">
                    <div className="logo-col">{renderUniLogo(uni)}</div>
                    <div className="name-col">
                      <strong>{uni.name}</strong>
                      {uni.slug && <span style={{ display: 'block', fontSize: '11px', color: '#64748b', fontWeight: '500' }}>/{uni.slug}</span>}
                    </div>
                    <div className="status-col">
                      <span className={`badge ${uni.is_active !== false ? 'active' : 'inactive'}`}>
                        {uni.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    {/* Guidelines Doc Column */}
                    <div className="doc-col">
                      {uni.document_url ? (
                        <>
                          <a
                            href={getFullDocUrl(uni.document_url)}
                            className="doc-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            📄 Guidelines PDF
                          </a>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>Change:</span>
                        </>
                      ) : (
                        <span className="no-doc">No document</span>
                      )}
                      
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        style={{ display: 'none' }}
                        ref={(el) => (quickFileInputRefs.current[uni.id] = el)}
                        onChange={(e) => handleQuickUpload(uni.id, e.target.files[0])}
                      />
                      <button
                        className="file-upload-btn"
                        onClick={() => quickFileInputRefs.current[uni.id]?.click()}
                      >
                        {uni.document_url ? 'Upload New' : '📁 Upload PDF/DOC'}
                      </button>
                    </div>

                    {/* Sample Report Column */}
                    <div className="doc-col">
                      {uni.sample_report_url ? (
                        <>
                          <a
                            href={getFullDocUrl(uni.sample_report_url)}
                            className="doc-link"
                            style={{ color: '#0284c7' }}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            📄 Sample Report
                          </a>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>Change:</span>
                        </>
                      ) : (
                        <span className="no-doc">No sample report</span>
                      )}
                      
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        style={{ display: 'none' }}
                        ref={(el) => (quickSampleFileInputRefs.current[uni.id] = el)}
                        onChange={(e) => handleQuickSampleUpload(uni.id, e.target.files[0])}
                      />
                      <button
                        className="file-upload-btn"
                        onClick={() => quickSampleFileInputRefs.current[uni.id]?.click()}
                      >
                        {uni.sample_report_url ? 'Upload New' : '📁 Upload Sample'}
                      </button>
                    </div>

                    <div className="action-col">
                      <button onClick={() => handleEdit(uni)}>Edit</button>
                      <button className="danger" onClick={() => handleDelete(uni.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminUniversities;
